import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useFormik } from 'formik';
import { Validators } from 'core';
import {
  SendCommunicationCustomersSelection as CustomersSelection,
  ServicesPassengersConcernedByCommunicationParams,
  ServicesPassengersConcernedByCommunicationResponse,
  TargetType,
} from 'types';
import {
  servicesPassengersConcernedByCommunicationActions,
  SendCommunicationSelectors,
  SendCommunicationActions,
  OtherSelectionSelectors,
  OtherSelectionActions,
  servicesPassengersConcernedByCommunicationSelectors,
} from 'common/config/store';
import { useIntl } from 'common/config/i18n';
import HeaderWithSteps from 'components/organisms/header-with-steps';
import ReminderSelections from 'components/organisms/reminder-selections';
import ServicesPassengersConcernedByCommunication from 'components/organisms/total-impacted-passengers';
import TargetCustomerAndStations from 'components/organisms/target-customer-stations';
import TargetCarriages from 'components/organisms/target-carriages';
import TargetOtherSelection from 'components/organisms/target-other-selection';
import Button from 'components/molecules/button';
import history from 'app-router/history';
import {
  calculateSelectedTargetType,
  calculateSubtitle,
  isCheckOthersWasSelected,
  prepareServicesPassengersConcernedByCommunicationParams,
} from 'common/utils/send-communication';
import {
  ButtonWrapper,
  NoPassengersModal,
  Props,
  SelectionWrapper,
  VerticalSpace,
  Wrapper,
  formikInitialValues,
} from './utils';

const SendCommunication: React.FC<Props> = ({ match }) => {
  const { t } = useIntl();
  const dispatch = useDispatch();

  const reminderSelectionsRef = useRef<HTMLDivElement>(null);
  const [reminderSelectionsHeight, setReminderSelectionsHeight] = useState<number>(0);
  const [showNoPassengersModal, setShowNoPassengersModal] = useState<boolean>(false);
  const [totalPassengersNeedsRefresh, setTotalPassengersNeedsRefresh] = useState<boolean>(false);

  const [checkOthers, setCheckOthers] = useState<boolean>(false);
  const [isOnlyConflictedTickets, setIsOnlyConflictedTickets] = useState<boolean>(false);
  const [isExcludeConflictedTickets, setIsExcludeConflictedTickets] = useState<boolean>(false);

  const [targetType, setTargetType] = useState<TargetType>(TargetType.CLIENT_TYPE);

  const [odsSummaryContent, setOdsSummaryContent] = useState<string[][]>([]);

  const typologies = useSelector(OtherSelectionSelectors.typologiesSelector);
  const conflictTypes = useSelector(OtherSelectionSelectors.conflictTypesSelector);
  const servicesPassengersConcernedByCommunicationData = useSelector(
    servicesPassengersConcernedByCommunicationSelectors.ServicesPassengersConcernedByCommunicationDataSelector
  );
  const servicesPassengersConcernedByCommunicationStatus = useSelector(
    servicesPassengersConcernedByCommunicationSelectors.ServicesPassengersConcernedByCommunicationStatusSelector
  );
  const ServicesPassengersConcernedByCommunicationErrorMessage = useSelector(
    servicesPassengersConcernedByCommunicationSelectors.ServicesPassengersConcernedByCommunicationErrorMessageSelector
  );
  const stationsConcerned = useSelector(SendCommunicationSelectors.stationsConcernedSelector);
  const selectedServiceType = useSelector(SendCommunicationSelectors.selectedServiceTypeSelector);
  const equipementCarriages = useSelector(SendCommunicationSelectors.equipementCarriagesSelector);
  const isBulkCommunication = useSelector(SendCommunicationSelectors.isBulkCommunicationSelector);
  const selectedServices = useSelector(SendCommunicationSelectors.selectedServicesSelector);
  const formValuesFirstStep = useSelector(SendCommunicationSelectors.firstStepFormValuesSelector);

  const { situationCategory } = match.params;

  const formik = useFormik({
    initialValues: formikInitialValues,
    validate: (values) => {
      return Validators.sendCommunication.FirstStep(values, {
        isOnlyConflictedTickets,
        isPurchaseComparisonSelected: !!values.selectedPurchaseComparison,
        customersSelection: values.customersSelection,
      });
    },
    onSubmit: (values) => {
      if (selectedServices) {
        if (selectedServices.length < 5) {
          refreshServicesPassengersConcernedByCommunication(
            (result?: ServicesPassengersConcernedByCommunicationResponse) => {
              if (result?.totalPassengers === 0) {
                setShowNoPassengersModal(true);
              } else {
                dispatch(SendCommunicationActions.submitStep1(values));
                history.push(`/send-communication-step-two/${situationCategory}`, {
                  odsSummaryContent,
                });
              }
            }
          );
        } else {
          dispatch(SendCommunicationActions.submitStep1(values));
          history.push(`/send-communication-step-two/${situationCategory}`, {
            odsSummaryContent,
          });
        }
      }
    },
  });

  useEffect(() => {
    dispatch(OtherSelectionActions.fetchTypologies());
    dispatch(OtherSelectionActions.fetchConflictTypes());
  }, [dispatch]);

  /**
   * Trigger the validation whenever isOnlyConflictedTickets, targetType and formik.values change
   * In order to pass the updated value to the validate method
   */
  useEffect(() => {
    formik.validateForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOnlyConflictedTickets, targetType, formik.values]);

  /**
   * this useEffect reinitialise the values when goBack from step 2 to step 1 (persistence need)
   */
  useEffect(() => {
    if (formValuesFirstStep) {
      formik.setFieldValue('DVNumbers', formValuesFirstStep?.DVNumbers);
      formik.setFieldValue('customersSelection', formValuesFirstStep?.customersSelection || CustomersSelection.All);
      formik.setFieldValue('impactedStations', formValuesFirstStep?.impactedStations || []);
      formik.setFieldValue('excludedStations', formValuesFirstStep?.excludedStations || []);
      formik.setFieldValue('selectedCarriages', formValuesFirstStep?.selectedCarriages || []);
      if ([false, true].includes(formValuesFirstStep?.includePassengersWithoutSeats)) {
        formik.setFieldValue('includePassengersWithoutSeats', formValuesFirstStep?.includePassengersWithoutSeats);
      } else {
        formik.setFieldValue('includePassengersWithoutSeats', true);
      }
      formik.setFieldValue('selectedTypologiesIds', formValuesFirstStep?.selectedTypologiesIds || []);
      formik.setFieldValue('selectedConflictTypeId', formValuesFirstStep?.selectedConflictTypeId || undefined);
      formik.setFieldValue('excludeAllConflictTypes', formValuesFirstStep?.excludeAllConflictTypes);
      formik.setFieldValue('selectedPurchaseComparison', formValuesFirstStep?.selectedPurchaseComparison);
      formik.setFieldValue('purchaseReferenceDate', formValuesFirstStep?.purchaseReferenceDate);
      formik.setFieldValue('purchaseReferenceTime', formValuesFirstStep?.purchaseReferenceTime);

      setTargetType(calculateSelectedTargetType(formValuesFirstStep));
      setCheckOthers(isCheckOthersWasSelected(formValuesFirstStep));
      setIsOnlyConflictedTickets(!!formValuesFirstStep.selectedConflictTypeId);
      setIsExcludeConflictedTickets(formValuesFirstStep.excludeAllConflictTypes);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formValuesFirstStep]);

  useEffect(() => {
    if (reminderSelectionsRef?.current?.clientHeight) {
      setReminderSelectionsHeight(reminderSelectionsRef?.current?.clientHeight);
    }
  }, [reminderSelectionsHeight, reminderSelectionsRef]);

  const refreshServicesPassengersConcernedByCommunication = (
    callback?: (data?: ServicesPassengersConcernedByCommunicationResponse) => void
  ): void => {
    if (selectedServices && selectedServices.length < 5) {
      const selectedServicesMapped = selectedServices.map(({ serviceName, circulationDate }) => ({
        serviceName,
        serviceDate: circulationDate,
      }));
      const requestParams: ServicesPassengersConcernedByCommunicationParams =
        prepareServicesPassengersConcernedByCommunicationParams(selectedServicesMapped, formik.values);
      if (callback) {
        dispatch(
          servicesPassengersConcernedByCommunicationActions.fetchServicesPassengersConcernedByCommunication(
            requestParams,
            callback
          )
        );
      } else {
        dispatch(
          servicesPassengersConcernedByCommunicationActions.fetchServicesPassengersConcernedByCommunication(
            requestParams
          )
        );
      }
      setTotalPassengersNeedsRefresh(false);
    }
  };

  const subtitle = calculateSubtitle({
    selectedServices,
    translate: t,
  });

  return (
    <form onSubmit={formik.handleSubmit}>
      {showNoPassengersModal && (
        <NoPassengersModal
          open={showNoPassengersModal}
          onCancel={() => setShowNoPassengersModal(false)}
          onConfirm={(isConfirm: boolean) => {
            setShowNoPassengersModal(false);
            if (isConfirm) {
              dispatch(SendCommunicationActions.submitStep1(formik.values));
              history.push(`/send-communication-step-two/${situationCategory}`, {
                odsSummaryContent,
              });
            }
          }}
        />
      )}
      <Wrapper>
        <HeaderWithSteps
          title={t('processus', situationCategory)}
          subtitle={subtitle}
          step={{
            current: 1,
            total: 2,
            currentStepLabel: t('sendCommunication', 'step-1'),
          }}
          isStickyTop
        >
          <>
            <SelectionWrapper>
              <div style={{ flex: 1 }}>
                <ReminderSelections
                  elemRef={reminderSelectionsRef}
                  formValues={formik.values}
                  isBulkCommunication={!!isBulkCommunication}
                />
              </div>
              <div style={{ width: 300 }}>
                <ServicesPassengersConcernedByCommunication
                  data={servicesPassengersConcernedByCommunicationData}
                  status={servicesPassengersConcernedByCommunicationStatus}
                  isAlertMode={
                    totalPassengersNeedsRefresh || servicesPassengersConcernedByCommunicationStatus === 'error'
                  }
                  onRefresh={refreshServicesPassengersConcernedByCommunication}
                  height={reminderSelectionsHeight}
                  step={1}
                  isNotAvailable={selectedServices && selectedServices?.length >= 5}
                />
              </div>
            </SelectionWrapper>
            <VerticalSpace />
          </>
        </HeaderWithSteps>
        <div>
          <TargetCustomerAndStations
            formik={formik}
            stationsConcerned={stationsConcerned}
            onTouch={() => setTotalPassengersNeedsRefresh(true)}
            isBulkCommunication={!!isBulkCommunication}
            targetType={targetType}
            setTargetType={setTargetType}
            ServicesPassengersConcernedByCommunicationErrorMessage={
              ServicesPassengersConcernedByCommunicationErrorMessage || undefined
            }
            setOdsSummaryContent={setOdsSummaryContent}
          />
          <TargetCarriages
            formik={formik}
            onTouch={() => setTotalPassengersNeedsRefresh(true)}
            selectedServiceType={selectedServiceType}
            equipementCarriages={equipementCarriages}
            isVisible={targetType === TargetType.CLIENT_TYPE}
          />
          <TargetOtherSelection
            formik={formik}
            typologies={typologies}
            conflictTypes={conflictTypes}
            onTouch={() => setTotalPassengersNeedsRefresh(true)}
            checkOthers={checkOthers}
            setCheckOthers={setCheckOthers}
            isOnlyConflictedTickets={isOnlyConflictedTickets}
            setIsOnlyConflictedTickets={setIsOnlyConflictedTickets}
            isExcludeConflictedTickets={isExcludeConflictedTickets}
            setIsExcludeConflictedTickets={setIsExcludeConflictedTickets}
            isVisible={targetType === TargetType.CLIENT_TYPE}
          />
          <ButtonWrapper isClientType={targetType === TargetType.CLIENT_TYPE}>
            <Button
              height="40px"
              width="170px"
              borderRadius="20px"
              disabled={!formik.isValid && Object.keys(formik.touched).length !== 0}
              htmlType="submit"
            >
              {t('Buttons', 'CONTINUE_BUTTON')}
            </Button>
          </ButtonWrapper>
        </div>
      </Wrapper>
    </form>
  );
};

export default SendCommunication;
